import * as React from 'react'
import { useState, useId } from 'react'
import { twMerge } from 'tailwind-merge'
import { Toggle } from '@highlight/ui'

interface CheckboxProps {
  checked?: boolean
  className?: string
  defaultChecked?: boolean
  disabled?: boolean
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  readOnly?: boolean
  label?: string | React.ReactNode
}

const Checkbox = ({ className, checked, defaultChecked, disabled, onChange, readOnly, label }: CheckboxProps) => {
  const isControlled = checked !== undefined
  const [uncontrolledChecked, setUncontrolledChecked] = useState(defaultChecked ?? false)
  const effectiveChecked = isControlled ? checked : uncontrolledChecked
  const id = useId()
  const effectiveDisabled = disabled || readOnly

  const handleCheckedChange = React.useCallback(
    (value: boolean) => {
      if (effectiveDisabled) return
      if (!isControlled) setUncontrolledChecked(value)
      onChange?.({ target: { checked: value } } as React.ChangeEvent<HTMLInputElement>)
    },
    [effectiveDisabled, isControlled, onChange],
  )

  return (
    <label
      htmlFor={id}
      className={twMerge(
        'inline-flex cursor-pointer items-center align-middle',
        effectiveDisabled && 'pointer-events-none cursor-default',
        className,
      )}
    >
      <Toggle
        id={id}
        data-slot="checkbox"
        checked={effectiveChecked}
        defaultChecked={!isControlled ? defaultChecked : undefined}
        disabled={effectiveDisabled}
        onCheckedChange={handleCheckedChange}
        className="shrink-0"
      />
      {label != null && <span className="hover:text-foreground ml-2 cursor-pointer text-sm">{label}</span>}
    </label>
  )
}

export default Checkbox
